import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tractor, ArrowLeft, ArrowRight, ChevronRight, ChevronDown, Search, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import FileUploader from "@/components/FileUploader";
import SwipeSuggestions from "@/components/SwipeSuggestions";
import ScanSerialButton from "@/components/ScanSerialButton";
import { formatApiError } from "@/lib/utils2";
import { toast } from "sonner";

export default function AddEquipment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [view, setView] = useState("industry"); // "industry" | "alphabetical"
  const [filter, setFilter] = useState("");
  const [type, setType] = useState(null);
  // Which industries are expanded. "Other" always open; others collapse on mobile.
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({
    name: "",
    equipment_type: "",
    category: "Home",
    usage_unit: "hours",
    year: "",
    color: "",
    serial_number: "",
    purchase_year: "",
    purchase_price: "",
    current_hours: "",
    special_instructions: "",
    photo_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [createdEquipment, setCreatedEquipment] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/catalog/equipment-types");
      setCatalog(data.items);
      setIndustries(data.industries || []);
    })();
  }, []);

  // Once we know the industries, set initial expand state:
  // On mobile, only "Other" open by default; on desktop all open.
  useEffect(() => {
    if (!industries.length) return;
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
    setExpanded((prev) => {
      // Preserve any existing user toggles
      if (Object.keys(prev).length) return prev;
      const next = {};
      industries.forEach((i) => {
        next[i] = isMobile ? i === "Other" : true;
      });
      return next;
    });
  }, [industries]);

  // When user searches, temporarily expand all matched groups so results are visible
  useEffect(() => {
    if (!filter.trim()) return;
    setExpanded((prev) => {
      const next = { ...prev };
      industries.forEach((i) => { next[i] = true; });
      return next;
    });
  }, [filter, industries]);

  const toggleIndustry = (name) => {
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Apply filter
  const filtered = useMemo(() => {
    if (!filter.trim()) return catalog;
    const q = filter.toLowerCase();
    return catalog.filter((c) =>
      c.type.toLowerCase().includes(q) ||
      (c.industry || "").toLowerCase().includes(q)
    );
  }, [catalog, filter]);

  const grouped = useMemo(() => {
    const map = new Map();
    industries.forEach((i) => map.set(i, []));
    filtered.forEach((c) => {
      const key = c.industry || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    // Pin "Other" to the top for quick access — especially important on mobile.
    const entries = Array.from(map.entries()).filter(([, arr]) => arr.length > 0);
    entries.sort(([a], [b]) => {
      if (a === "Other") return -1;
      if (b === "Other") return 1;
      return 0;
    });
    return entries;
  }, [filtered, industries]);

  const sortedAlpha = useMemo(
    () => [...filtered].sort((a, b) => a.type.localeCompare(b.type)),
    [filtered]
  );

  const chooseType = (entry) => {
    setType(entry);
    setForm((f) => ({
      ...f,
      equipment_type: entry.type,
      category: entry.category,
      usage_unit: entry.usage_unit || "hours",
      name: f.name || entry.type,
    }));
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const submitDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      ["year", "purchase_year"].forEach((k) => { body[k] = body[k] ? parseInt(body[k]) : null; });
      ["purchase_price", "current_hours"].forEach((k) => { body[k] = body[k] ? parseFloat(body[k]) : null; });
      const { data } = await api.post("/equipment?auto_schedule=false", body);
      setCreatedEquipment(data);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const onSuggestionsDone = async (kept) => {
    if (!createdEquipment) return;
    if (kept.length > 0) {
      try {
        const items = kept.map((s) => ({
          equipment_id: createdEquipment.equipment_id,
          task: s.name,
          due_date: new Date(Date.now() + (s.unit === "days" ? s.interval : 30) * 86400000).toISOString(),
          interval: s.interval,
          unit: s.unit,
          recurring: true,
          reminder_on: true,
          notes: s.notes || null,
        }));
        await api.post("/schedule/bulk", items);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    toast.success(`${createdEquipment.name} added to garage`);
    navigate(`/app/equipment/${createdEquipment.equipment_id}`);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-4xl mx-auto">
      <Link to="/app/equipment" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-500 inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to garage
      </Link>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 text-[10px] uppercase tracking-[0.3em]">
        {["Type", "Details", "Schedule"].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <React.Fragment key={label}>
              <div className={`flex items-center gap-2 ${active ? "text-orange-500" : done ? "text-zinc-300" : "text-zinc-600"}`}>
                <div className={`h-6 w-6 rounded-sm grid place-items-center font-bold text-xs ${active ? "bg-orange-600 text-white" : done ? "bg-zinc-700 text-white" : "border border-zinc-800"}`}>
                  {n}
                </div>
                <span>{label}</span>
              </div>
              {i < 2 && <div className={`flex-1 h-px ${done ? "bg-zinc-600" : "bg-zinc-900"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div data-testid="step-type">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// 01 Select type</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-2">
            What are you maintaining?
          </h1>
          <p className="text-zinc-400 mb-6">Tap a card to continue — we'll autofill the category and suggested service intervals.</p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1">
                <TabsTrigger value="industry" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="view-industry">By industry</TabsTrigger>
                <TabsTrigger value="alphabetical" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="view-alpha">A → Z</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search equipment types..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="catalog-search"
              />
            </div>
          </div>

          {view === "industry" ? (
            <div className="space-y-3">
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  onClick={() => {
                    const allOpen = industries.every((i) => expanded[i] !== false);
                    const next = {};
                    industries.forEach((i) => { next[i] = !allOpen; });
                    // Always keep Other expanded
                    next.Other = true;
                    setExpanded(next);
                  }}
                  className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-orange-500"
                  data-testid="industry-toggle-all"
                >
                  {industries.every((i) => expanded[i] !== false) ? "Collapse all" : "Expand all"}
                </button>
              </div>
              {grouped.map(([industry, items]) => {
                const isOpen = expanded[industry] !== false;
                const isOther = industry === "Other";
                return (
                  <div
                    key={industry}
                    className={`border rounded-sm overflow-hidden ${isOther ? "border-orange-600/40 bg-orange-950/10" : "border-zinc-800 bg-zinc-900/30"}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleIndustry(industry)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900/60 transition-colors text-left"
                      data-testid={`industry-toggle-${industry}`}
                      aria-expanded={isOpen}
                    >
                      {isOther ? (
                        <Wrench className="w-4 h-4 text-orange-500" />
                      ) : (
                        <div className="h-4 w-1 bg-orange-500" />
                      )}
                      <div className="flex-1 text-[11px] uppercase tracking-[0.3em] font-bold">
                        {isOther ? <span className="text-orange-500">Custom / Other · pinned</span> : <span className="text-zinc-300">{industry}</span>}
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{items.length}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${isOpen ? "rotate-180 text-orange-500" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border-t border-zinc-800 bg-zinc-950/40">
                        {items.map((c) => <TypeCard key={c.type} c={c} onClick={() => chooseType(c)} />)}
                      </div>
                    )}
                  </div>
                );
              })}
              {grouped.length === 0 && (
                <div className="text-center text-zinc-500 py-12">No matches for "{filter}"</div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sortedAlpha.map((c) => <TypeCard key={c.type} c={c} onClick={() => chooseType(c)} />)}
              {sortedAlpha.length === 0 && (
                <div className="col-span-full text-center text-zinc-500 py-12">No matches for "{filter}"</div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 2 && type && (
        <div data-testid="step-details">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// 02 Equipment details</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-2">
            Tell us about your <span className="text-orange-500">{type.type.toLowerCase()}</span>.
          </h1>
          <p className="text-zinc-400 mb-8">Year, color, serial # — all optional but great for records & resale.</p>

          <form onSubmit={submitDetails} className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-zinc-900 border border-zinc-800 rounded-sm p-6 sm:p-8">
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name / nickname</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Red Combine, North 40 Tractor"
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="field-name"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 focus:ring-orange-500 rounded-sm" data-testid="field-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="Home">Home (personal)</SelectItem>
                  <SelectItem value="Work">Work (tax deductible)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Year</Label>
              <Input
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="field-year"
                placeholder="2019"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Color</Label>
              <Input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="field-color"
                placeholder="Red"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Serial number</Label>
                <ScanSerialButton onResult={(t) => setForm({ ...form, serial_number: t })} />
              </div>
              <Input
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm font-mono"
                data-testid="field-serial"
                placeholder="Type or scan from plate"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {form.usage_unit === "miles" ? "Current mileage" : form.usage_unit === "hours" ? "Current hours" : "Last serviced"}
              </Label>
              {form.usage_unit === "none" ? (
                <Input
                  type="date"
                  value={form.current_hours}
                  onChange={(e) => setForm({ ...form, current_hours: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm font-mono"
                  data-testid="field-hours"
                />
              ) : (
                <Input
                  type="number"
                  value={form.current_hours}
                  onChange={(e) => setForm({ ...form, current_hours: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm font-mono"
                  data-testid="field-hours"
                  placeholder={form.usage_unit === "miles" ? "45,000" : "325"}
                />
              )}
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Purchase year</Label>
              <Input
                type="number"
                value={form.purchase_year}
                onChange={(e) => setForm({ ...form, purchase_year: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="field-purchase-year"
              />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Purchase price ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.purchase_price}
                onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="field-purchase-price"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Special instructions</Label>
              <Textarea
                value={form.special_instructions}
                onChange={(e) => setForm({ ...form, special_instructions: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                rows={3}
                placeholder="e.g. Runs best on premium, check tire pressure before starting..."
                data-testid="field-instructions"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Photos</Label>
              <FileUploader
                value={form.photo_ids}
                onChange={(ids) => setForm({ ...form, photo_ids: ids })}
                accept="image/*"
                max={6}
                label="Equipment photos"
              />
            </div>

            <div className="sm:col-span-2 flex justify-between pt-2">
              <Button type="button" variant="ghost" onClick={() => setStep(1)} className="text-zinc-400 hover:text-white" data-testid="back-to-type">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold"
                data-testid="save-equipment"
              >
                {saving ? "Saving..." : "Next: Pick schedule"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </form>
        </div>
      )}

      {step === 3 && createdEquipment && type && (
        <div data-testid="step-schedule">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// 03 Build schedule</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-2">
            Swipe to build a schedule.
          </h1>
          <p className="text-zinc-400 mb-8">
            Swipe right on tasks to add to <span className="text-orange-500">{createdEquipment.name}</span>. Swipe left to skip.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 sm:p-8">
            <SwipeSuggestions suggestions={type.suggested} onFinish={onSuggestionsDone} />
          </div>
        </div>
      )}
    </div>
  );
}

function TypeCard({ c, onClick }) {
  return (
    <button
      onClick={onClick}
      type="button"
      data-testid={`pick-type-${c.type}`}
      className="group relative p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-600 hover:bg-zinc-800/50 hover:-translate-y-0.5 rounded-sm text-left transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <Tractor className="w-6 h-6 text-zinc-500 group-hover:text-orange-500 transition-colors" />
        <ChevronRight className="w-5 h-5 text-zinc-700 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
      </div>
      <div className="font-display font-bold text-lg tracking-tight mb-1 leading-tight">{c.type}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 group-hover:text-zinc-400">
        {c.category} · {c.suggested.length} tasks
      </div>
      <div className="absolute inset-x-0 bottom-0 h-[2px] bg-orange-600 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
    </button>
  );
}
